import React, { type FC } from "react";
import { useLocation } from "react-router-dom";

import { Button } from "~/components/button";
import { IconAssistant } from "~/components/icon";
import { Truncated } from "~/components/truncated";
import { useCapture } from "~/tools/analytics/use-capture";

import { SystemNotificationCenter } from "../../components/_system-notifications";
import { DocLink } from "../../components/react/docLink/DocLink";
import { useExplorationDrawerState } from "../../components/react/drawer/use-exploration-drawer-state";
import { UpgradeButton } from "../../components/react/upgrade-button/UpgradeButton";
import { matchPath } from "../../generic/routing";
import { queryLicenseConfig } from "../../queries";
import { useQueryTopNavCtaIntroText } from "../../queries/_contentful";
import { useQuery } from "../../queries/react-query";
import { COMPONENT_PATH } from "../../routes/Routes";
import { NBSP } from "../../routes/welcome-page/WelcomeLayout";
import { useAiAssistantAvailability } from "../../sessions/use-ai-assistant-availability";
import { AccountInfo } from "../AccountInfo/AccountInfo";

import { OnlineTrialMessage } from "./OnlineTrialMessage";
import { TopNav } from "./TopNav";

export interface TopNavigationProps {
	isFormAuth: boolean;
	isPlayground: boolean;
}

export const TopNavigation: FC<TopNavigationProps> = ({ isFormAuth, isPlayground }) => {
	const drawerState = useExplorationDrawerState();
	const location = useLocation();
	const componentIdentifier = matchPath(location.pathname, COMPONENT_PATH)?.params.identifier;

	const {
		data: { isTrial, isCommunity, daysLeftToExpire } = {
			isTrial: false,
			isCommunity: false,
			daysLeftToExpire: undefined,
		},
	} = useQuery(queryLicenseConfig);

	const enableAi = useAiAssistantAvailability();

	return (
		<div className="absolute inset-x-0 top-0 z-100 h-10 bg-core-layer-brand-secondary" data-testid="top-navigation">
			<nav className="flex flex-row items-center text-13 text-white leading-none">
				<TopNav />
				<div className="ml-auto flex shrink-0 flex-row items-center justify-end gap-4 pr-1">
					{isCommunity && <UpgradeToRancherPrimeAction className="mr-2" />}
					{isPlayground && <PlaygroundCallToAction />}
					{isTrial && daysLeftToExpire != null && <OnlineTrialMessage daysLeftToExpire={daysLeftToExpire} />}
					{enableAi && (
						<Button
							data-testid="assistant-chat-button"
							icon
							onClick={() => drawerState.openAiChat(componentIdentifier)}
						>
							<IconAssistant className="h-5 w-5" />
						</Button>
					)}
					<SystemNotificationCenter />
					<div data-testid="user-account">
						<AccountInfo guiAuth={isFormAuth} />
					</div>
				</div>
			</nav>
		</div>
	);
};

const UpgradeToRancherPrimeAction: FC<{ className?: string }> = ({ className }) => {
	const capture = useCapture();

	return (
		<Truncated className={className}>
			<DocLink
				capture={[capture.clickOnUpgradeToPrime, { location: "navbar" }]}
				slug="rancher-prime-upgrade"
				theme="light"
			>
				Upgrade to Rancher Prime
			</DocLink>{" "}
			<span className="text-grey-500">and enjoy faster troubleshooting</span>
		</Truncated>
	);
};

const PlaygroundCallToAction: FC = () => {
	const { data: ctaText } = useQueryTopNavCtaIntroText({ select: ({ fields }) => fields });

	return (
		<div className="flex h-10 flex-row items-center overflow-hidden">
			<Truncated data-testid="upgrade-button-intro-text">{ctaText?.value ?? NBSP}</Truncated>
			<div className="px-4" data-testid="upgrade-button">
				<UpgradeButton />
			</div>
		</div>
	);
};
